import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import MapIcon from '@mui/icons-material/Map';
import FloatingActionButton from '#component/device/general/floating-action-button';
import { fetchDevices, changePage, clear } from '#action/device/devices-paginated';
import { updateBreadcrumb } from '#action/breadcrumb';
import { fetchMeta } from '#action/meta/device';
import NotFoundMessage from '#component/general/not-found-message';
import FilterWrapper from '#component/general/list-filter/filter-wrapper';
import { useListFilterDataLoading } from '#component/general/list-filter/hooks';
import PreloaderComponent from '#component/preloader';
import { isFilterExist } from '#component/general/list-filter/helper';
import filterConfig from './filter/config';
import ViewSwitcher from './view-switcher';
import { getSearchParams } from './helper';
import { useDevicesLocalStorageSorting } from './hooks';
import {
  StyledPagination,
  PaginationPlaceholder,
  FilterButtonContainer,
  SwitcherContainer,
  DeviceMobileFilter
} from './styles';
import CardsView from './cards-view/detailed-card-view';
import TableViewNew from './table-view/table-view-new';
import { useError, useUrlPagination } from '#hooks';
import { setDocumentTitle } from '#helper';

/**
 * Renders device dashboard
 * @returns {React.FC}
 */

const Dashboard = props => {
  const { history } = props;
  const [loading, setLoading] = useState(true);
  const [fabHasMarginBottom, setFabHasMarginBottom] = useState(false);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    devices,
    page,
    pages,
    status,
    error
  } = useSelector(s => s.devicesPaginated, shallowEqual);
  const [withoutDevices, setWithoutDevices] = useState(false);
  const listFilter = useSelector(s => s.listFilter, shallowEqual);
  const filterLoaded = listFilter.status === 'succeeded';
  const filterData = useMemo(() => filterConfig, []);
  const isDevicesLoading = status !== 'succeeded' || status === 'idle' || loading || !filterLoaded;
  const showFloatingButton = Boolean(devices.length && !isDevicesLoading);

  useEffect(() => {
    const deviceLoading = status !== 'succeeded' || status === 'idle';

    if (!deviceLoading) {
      const filterExist = isFilterExist(listFilter?.device ?? {});
      const emptyDevices = devices.length === 0;

      if (!filterExist && emptyDevices && listFilter.applied) {
        setWithoutDevices(true);
      }
    }
  }, [
    devices.length,
    listFilter.device,
    listFilter.applied,
    status
  ]);

  useEffect(() => {
    const handleScroll = () => {
      const scrolledFromTop = window.scrollY;
      const totalPageHeight = document.body.scrollHeight;
      const viewableArea = window.innerHeight;

      if (scrolledFromTop + viewableArea + 40 >= totalPageHeight) {
        setFabHasMarginBottom(true);
      } else {
        setFabHasMarginBottom(false);
      }
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const replaceHistoryParams = useCallback(
    newParams =>
      new Promise(res => {
        const { location: { search } } = history;
        const params = getSearchParams(search, newParams);

        history.replace(`?${params}`);
        res(true);
      }),
    [history]
  );

  useError(error);
  useUrlPagination({ page, pages, changePage });
  useDevicesLocalStorageSorting(replaceHistoryParams);

  useEffect(() => {
    setDocumentTitle('Devices overview');
    dispatch(updateBreadcrumb([{ label: 'Devices' }]));
  }, [dispatch]);

  const fetchData = useCallback(async () => {
    await dispatch(fetchMeta());
    await dispatch(fetchDevices());
    setLoading(false);
  }, [dispatch]);

  const clearData = useCallback(() => {
    dispatch(clear());
  }, [dispatch]);

  useListFilterDataLoading(fetchData, clearData);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const handlePageChange = async (event, newPage) => {
    setLoading(true);
    if (page !== newPage) {
      await replaceHistoryParams({ filterType: 'page', value: newPage });
      await dispatch(changePage(newPage));
    }
    setLoading(false);
  };

  if (error) {
    return null;
  }

  if (withoutDevices) {
    return (
      <NotFoundMessage>
        All your devices will appear here soon
      </NotFoundMessage>
    );
  }

  return (
    <section>
      {!isMobile &&
      <FilterButtonContainer>
        <FilterWrapper data={filterData} />
        <SwitcherContainer>
          <ViewSwitcher tab={0} />
        </SwitcherContainer>
      </FilterButtonContainer>}
      {isMobile ? (
        status === 'loading' ?
          (
            <>
              <DeviceMobileFilter>
                <FilterWrapper data={filterData} />
              </DeviceMobileFilter>
              <PreloaderComponent />
            </>
          ) :
            <>
              <DeviceMobileFilter>
                <FilterWrapper data={filterData} />
              </DeviceMobileFilter>
              <CardsView replaceHistoryParams={replaceHistoryParams} />
              {fabHasMarginBottom && showFloatingButton && <FloatingActionButton
                buttonColor="secondary"
                onClickFabButton={() => history.push('/device-map?order=asc&orderBy=title&occupied=all')}
                buttonIcon={<MapIcon />}
                isPagePaginated={true}
                buttonName="Map"
                textColor="white"
              />}
              {!fabHasMarginBottom && showFloatingButton && <FloatingActionButton
                buttonColor="secondary"
                onClickFabButton={() => history.push('/device-map?order=asc&orderBy=title&occupied=all')}
                buttonIcon={<MapIcon />}
                buttonName="Map"
                textColor="white"
              />}
            </>
      ) : (
        <TableViewNew replaceHistoryParams={replaceHistoryParams} />
      )}
      {(!!pages && Number(pages) > 1) && status !== 'loading' ? (
        <StyledPagination
          shape="rounded"
          color="secondary"
          count={pages}
          page={page}
          onChange={handlePageChange}
        />
      ) : (
        <PaginationPlaceholder />
      )}
    </section>
  );
};

export default Dashboard;
